import { jwtDecode } from 'jwt-decode';

interface JWTPayload {
  userId: number;
  exp: number;
  iat: number;
}

interface SessionConfig {
  warningBeforeExpiration: number; // minutes before expiration to show warning
  idleTimeout: number; // minutes of inactivity before logout
  checkInterval: number; // seconds between session checks
}

interface SessionEventCallbacks {
  onSessionExpired?: () => void;
  onIdleTimeout?: () => void;
  onSessionWarning?: (minutesLeft: number) => void;
  onSessionExtended?: () => void;
}

interface AuthService {
  refreshAccessToken(): Promise<boolean>;
}

class SessionManager {
  private static instance: SessionManager;
  private config: SessionConfig;
  private callbacks: SessionEventCallbacks;
  private lastActivity: number;
  private sessionCheckInterval: NodeJS.Timeout | null = null;
  private activityListeners: (() => void)[] = [];
  private authService: AuthService | null = null;
  private isRefreshing: boolean = false;
  
  // Default configuration
  private static DEFAULT_CONFIG: SessionConfig = {
    warningBeforeExpiration: 5, // 5 minutes warning
    idleTimeout: 30, // 30 minutes idle timeout
    checkInterval: 60 // check every 60 seconds
  };

  private constructor(config?: Partial<SessionConfig>, callbacks?: SessionEventCallbacks) {
    this.config = { ...SessionManager.DEFAULT_CONFIG, ...config };
    this.callbacks = callbacks || {};
    this.lastActivity = Date.now();
    this.initializeActivityTracking();
    this.startSessionMonitoring();
  }

  public static getInstance(config?: Partial<SessionConfig>, callbacks?: SessionEventCallbacks): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager(config, callbacks);
    }
    return SessionManager.instance;
  }

  /**
   * Set the auth service for automatic token refresh
   */
  public setAuthService(authService: AuthService): void {
    this.authService = authService;
  }

  /**
   * Initialize activity tracking for idle timeout
   */
  private initializeActivityTracking(): void {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const updateActivity = () => {
      this.lastActivity = Date.now();
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, true);
      this.activityListeners.push(() => document.removeEventListener(event, updateActivity, true));
    });
  }

  /**
   * Start monitoring session status
   */
  private startSessionMonitoring(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }

    this.sessionCheckInterval = setInterval(() => {
      this.checkSessionStatus();
    }, this.config.checkInterval * 1000);

    // Initial check
    this.checkSessionStatus();
  }

  /**
   * Check current session status
   */
  private checkSessionStatus(): void {
    const token = this.getToken();
    
    if (!token) {
      return; // No token, no session to check
    }

    try {
      const tokenExpiration = this.getTokenExpiration(token);
      const now = Date.now();
      const idleTime = now - this.lastActivity;
      
      // Check idle timeout
      if (idleTime > this.config.idleTimeout * 60 * 1000) {
        console.warn('⏰ Session expired due to inactivity');
        this.handleIdleTimeout();
        return;
      }

      // Check token expiration
      if (tokenExpiration && tokenExpiration <= now) {
        console.warn('🔒 Token has expired');
        this.handleSessionExpired();
        return;
      }

      // Check if we should attempt automatic refresh
      if (tokenExpiration && this.authService && !this.isRefreshing) {
        const timeLeft = tokenExpiration - now;
        const minutesLeft = Math.floor(timeLeft / (1000 * 60));
        
        // Attempt automatic refresh when token expires in 5 minutes or less
        if (minutesLeft <= this.config.warningBeforeExpiration && minutesLeft > 0) {
          console.log(`🔄 Token expires in ${minutesLeft} minutes, attempting automatic refresh...`);
          this.attemptAutoRefresh();
        }
      }
    } catch (error) {
      console.error('Error checking session status:', error);
      // If we can't decode the token, it's likely invalid
      this.handleSessionExpired();
    }
  }

  /**
   * Get token from localStorage
   */
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  /**
   * Parse JWT token and get expiration timestamp
   */
  private getTokenExpiration(token: string): number | null {
    try {
      const decoded = jwtDecode<JWTPayload>(token);
      return decoded.exp * 1000; // Convert to milliseconds
    } catch (error) {
      console.error('Failed to decode JWT token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  public isTokenExpired(token?: string): boolean {
    const tokenToCheck = token || this.getToken();
    if (!tokenToCheck) return true;

    const expiration = this.getTokenExpiration(tokenToCheck);
    if (!expiration) return true;

    return expiration <= Date.now();
  }

  /**
   * Check if session is idle
   */
  public isSessionIdle(): boolean {
    const idleTime = Date.now() - this.lastActivity;
    return idleTime > this.config.idleTimeout * 60 * 1000;
  }

  /**
   * Get remaining session time in minutes
   */
  public getRemainingTime(): number | null {
    const token = this.getToken();
    if (!token) return null;

    const expiration = this.getTokenExpiration(token);
    if (!expiration) return null;

    const remaining = expiration - Date.now();
    return Math.max(0, Math.floor(remaining / (1000 * 60)));
  }

  /**
   * Get idle time in minutes
   */
  public getIdleTime(): number {
    const idleTime = Date.now() - this.lastActivity;
    return Math.floor(idleTime / (1000 * 60));
  }

  /**
   * Handle session expiration
   */
  private handleSessionExpired(): void {
    this.clearSession();
    this.callbacks.onSessionExpired?.();
  }

  /**
   * Handle idle timeout
   */
  private handleIdleTimeout(): void {
    this.clearSession();
    this.callbacks.onIdleTimeout?.();
  }

  /**
   * Attempt automatic token refresh
   */
  private async attemptAutoRefresh(): Promise<void> {
    if (this.isRefreshing || !this.authService) {
      return;
    }

    try {
      this.isRefreshing = true;
      console.log('🔄 Attempting automatic token refresh...');
      
      const refreshSuccess = await this.authService.refreshAccessToken();
      
      if (refreshSuccess) {
        console.log('✅ Token refreshed automatically');
        this.lastActivity = Date.now(); // Reset activity timer
        this.callbacks.onSessionExtended?.();
      } else {
        console.warn('❌ Automatic token refresh failed');
        this.handleSessionExpired();
      }
    } catch (error) {
      console.error('Error during automatic refresh:', error);
      this.handleSessionExpired();
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Extend session (refresh activity)
   */
  public extendSession(): void {
    this.lastActivity = Date.now();
    this.callbacks.onSessionExtended?.();
    console.log('🔄 Session extended');
  }

  /**
   * Clear session data and stop monitoring
   */
  private clearSession(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
  }

  /**
   * Start session for a new user
   */
  public startSession(token: string, user: any): void {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    this.lastActivity = Date.now();
    this.startSessionMonitoring();
    console.log('✅ Session started');
  }

  /**
   * End session (logout)
   */
  public endSession(): void {
    this.clearSession();
    console.log('👋 Session ended');
  }

  /**
   * Update session callbacks
   */
  public updateCallbacks(callbacks: SessionEventCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Update session configuration
   */
  public updateConfig(config: Partial<SessionConfig>): void {
    this.config = { ...this.config, ...config };
    
    // Restart monitoring with new config
    this.startSessionMonitoring();
  }

  /**
   * Get session information
   */
  public getSessionInfo() {
    const token = this.getToken();
    return {
      isActive: !!token && !this.isTokenExpired(token),
      isIdle: this.isSessionIdle(),
      remainingTime: this.getRemainingTime(),
      idleTime: this.getIdleTime(),
      lastActivity: new Date(this.lastActivity),
      config: this.config
    };
  }

  /**
   * Cleanup when component unmounts
   */
  public destroy(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
    
    // Remove activity listeners
    this.activityListeners.forEach(removeListener => removeListener());
    this.activityListeners = [];
    
    SessionManager.instance = null as any;
  }
}

export default SessionManager;